/**
 * BottomNav v4 — SVG Lucide icons + active dot indicator.
 * Five tabs: Wallet · Discovery · Chat · Tools · Settings.
 * Tools replaces the old standalone Governance tab — Governance, Create
 * Account, Create Token, Keys/Permissions and Multisig all live inside it
 * now (ToolsScreen.tsx), so the dot stays lit while any of those is open.
 */

import type { TranslationKey } from '@properwallet/i18n'
import { useWalletStore, type WalletScreen } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import { useAppStore } from '../store/app.store'

// Inline SVG icons (Lucide, stroke-based, no dep needed)
const WalletIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2"/>
    <path d="M16 12h2"/>
    <path d="M2 10h20"/>
  </svg>
)

// Phantom-style 2×2 grid icon for Discovery
const GridIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1.5"/>
    <rect x="14" y="3" width="7" height="7" rx="1.5"/>
    <rect x="3" y="14" width="7" height="7" rx="1.5"/>
    <rect x="14" y="14" width="7" height="7" rx="1.5"/>
  </svg>
)

const ChatIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
  </svg>
)

// Wrench-style icon for the Tools tab (Create Account, Create Token,
// Governance, Keys, Multisig all live inside it now).
const ToolsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>
)

const SettingsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
)

const tabs: Array<{
  screen: WalletScreen
  Icon: () => JSX.Element
  labelKey: TranslationKey
}> = [
  { screen: 'wallet',     Icon: WalletIcon,     labelKey: 'nav.wallet' },
  { screen: 'discovery',  Icon: GridIcon,       labelKey: 'nav.discovery' },
  { screen: 'messages',   Icon: ChatIcon,       labelKey: 'nav.chat' },
  { screen: 'tools',      Icon: ToolsIcon,      labelKey: 'nav.tools' },
  { screen: 'settings',   Icon: SettingsIcon,   labelKey: 'nav.settings' },
]

export default function BottomNav() {
  const { currentScreen, navigate } = useWalletStore()
  const { t } = useTranslation()
  const mechatUnread = useAppStore(s => s.mechatUnread)

  return (
    <nav
      className="flex-shrink-0 nav-bottom"
      style={{
        background: '#0c100c',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div className="flex">
        {tabs.map(({ screen, Icon, labelKey }) => {
          const active =
            screen === 'tools'
              ? ['tools', 'governance', 'create-account', 'create-token', 'keys', 'multisig'].includes(currentScreen)
              : currentScreen === screen
          return (
            <button
              key={screen}
              onClick={() => navigate(screen)}
              className="flex-1 flex flex-col items-center py-2 gap-1 transition-colors"
              style={{ color: active ? '#5cbf63' : '#6b7280', background: 'none', border: 'none', cursor: 'pointer', minWidth: 0 }}
            >
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <Icon />
                {/* Unread indicator — only on the Chat tab, and not while it is open */}
                {screen === 'messages' && mechatUnread > 0 && !active && (
                  <span
                    aria-label={String(mechatUnread)}
                    style={{
                      position: 'absolute',
                      top: -1,
                      right: -3,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#43a047',
                      boxShadow: '0 0 0 2px #0c100c',
                    }}
                  />
                )}
              </span>
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 400, letterSpacing: '0.01em', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap' }}>
                {t(labelKey)}
              </span>
              {/* Active dot indicator */}
              {active && (
                <div style={{
                  width: 4, height: 4, borderRadius: '50%',
                  background: '#43a047',
                  marginTop: -2,
                }} />
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
